import { Badge } from "@/components/ui/badge";
import { Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DashboardPanel } from "@/components/dashboard/DashboardPanel";

interface Lead {
  id: string;
  contact_name: string;
  lastContact: Date;
  estimated_value: number;
  status: string;
}

interface TopLeadsCardProps {
  leads: Lead[];
}

export const TopLeadsCard = ({ leads }: TopLeadsCardProps) => {
  const navigate = useNavigate();

  const getDaysAgo = (date: Date) => {
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    return days === 0 ? "Today" : `${days}d ago`;
  };

  const getUrgencyColor = (days: number) => {
    if (days > 7) return "border-destructive/20 bg-destructive/10 text-destructive";
    if (days > 3) return "border-warning/20 bg-warning/10 text-warning";
    return "border-success/20 bg-success/10 text-success";
  };

  const topLeads = leads.slice(0, 3);

  if (topLeads.length === 0) {
    return (
      <DashboardPanel eyebrow="Follow-up" title="Top Priority Leads" className="bg-gradient-to-br from-card via-card to-primary/5">
          <p className="py-2 text-xs text-muted-foreground">
            No leads need immediate attention
          </p>
      </DashboardPanel>
    );
  }

  return (
    <DashboardPanel eyebrow="Follow-up" title="Top Priority Leads" className="bg-gradient-to-br from-card via-card to-primary/5">
        <div className="space-y-2.5">
          {topLeads.map((lead) => {
            const daysAgo = Math.floor((Date.now() - lead.lastContact.getTime()) / (1000 * 60 * 60 * 24));
            return (
              <div
                key={lead.id}
                className="group flex cursor-pointer items-center justify-between rounded-xl border border-border/70 bg-background/80 p-3 transition-all hover:border-primary/35 hover:bg-accent/50"
                onClick={() => navigate(`/leads/${lead.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{lead.contact_name}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground">
                      {getDaysAgo(lead.lastContact)}
                    </span>
                    <Badge className={`border text-[10px] ${getUrgencyColor(daysAgo)}`}>
                      ${(lead.estimated_value / 1000).toFixed(1)}K
                    </Badge>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            );
          })}
        </div>
    </DashboardPanel>
  );
};
